import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/shared/ui/card'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import TaskLogForm from '@/features/task-logging/TaskLogForm'
import { buildLotValidityWarnings } from '@/features/task-logging/buildLotValidityWarnings'
import {
  TASKS_DASHBOARD_RELOAD_ONLY,
  reloadTasksDashboardProps,
} from '@/shared/lib/reloadTasksDashboardProps'
import TaskDayOccurrencesDialog, {
  type TaskDayOccurrencesModalState,
} from '@/pages/Instruments/components/ReportsTab/TaskDayOccurrencesDialog'
import { FrequencyContextPanel } from '@/shared/ui/frequency-context-panel'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'
import { TaskNotesPanel } from '@/features/task-notes/TaskNotesPanel'
import { tasksDashboardDataFieldsPath, tasksDashboardLogContextPath } from '@/shared/lib/routes'
import apiClient from '@/shared/lib/apiClient'
import { useTaskNotes } from '@/shared/hooks/useTaskNotes'
import type WebDashboardTaskQueueCard from '@/shared/types/web/WebDashboardTaskQueueCard'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import type WebDashboardTaskLogContext from '@/shared/types/web/WebDashboardTaskLogContext'
import type { LoggerOption, LotRow } from '@/features/task-logging/TaskLogForm'
import type { SharedProps } from '@/shared/types/SharedProps'
import { showsFailedNeedsRerunRibbon } from '@/shared/lib/showsFailedNeedsRerunRibbon'
import { STATUS_PALETTE } from '@/shared/constants/statusPalette'
import { ControlsReagentsLotsModal } from '@/features/instrument-controls-reagents/ControlsReagentsLotsModal'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

// After Dashboard lot select/create the instrument Show props are absent; flash/errors still
// ride the shared Inertia payload. TaskQueueCard refetches log_context via onLotsMutation.
const DASHBOARD_LOT_MODAL_INERTIA_RELOAD_ONLY = ['errors', 'flash'] as const

// Task-status scheduling scale (NextGen Standard §06): keep legacy blue→green→red so the
// queue reads the SAME as TaskStatusBadge — RDY blue, DNW green, ODE red. Previously the queue
// used green=Ready / amber=Due Now / destructive=Overdue, making green mean opposite things on
// the dashboard vs the instrument page. Hues are derived from the core palette (single source).
const OVERDUE_SECTION = {
  label: () => t('taskQueue.sections.overdue'),
  cardClass: `${STATUS_PALETTE.red.border} bg-red-50`,
  icon: <AlertTriangle className={`h-4 w-4 ${STATUS_PALETTE.red.coreText}`} />,
} as const

const SECTION_CONFIG = {
  ODE: OVERDUE_SECTION,
  // Queue UTD rows (open NC / failed QC) paint under Overdue for Log Task (REBUILD-252).
  UTD: OVERDUE_SECTION,
  DNW: {
    label: () => t('taskQueue.sections.dueNow'),
    cardClass: `${STATUS_PALETTE.green.border} bg-green-50`,
    icon: <Clock className={`h-4 w-4 ${STATUS_PALETTE.green.coreText}`} />,
  },
  RDY: {
    label: () => t('taskQueue.sections.ready'),
    cardClass: `${STATUS_PALETTE.blue.border} bg-blue-50`,
    icon: <CheckCircle className={`h-4 w-4 ${STATUS_PALETTE.blue.coreText}`} />,
  },
} as const

type StatusCode = keyof typeof SECTION_CONFIG

const dataFieldsCache = new Map<number, WebInstrumentsTaskLogDataField[]>()

interface Props {
  record: WebDashboardTaskQueueCard
  loggerOptions: LoggerOption[]
  logAuthenticationCode: string | null
  hideInstrumentHeader?: boolean
}

const TaskQueueCard = ({
  record,
  loggerOptions,
  logAuthenticationCode,
  hideInstrumentHeader = false,
}: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const [logOpen, setLogOpen] = useState(false)
  const [viewDetailsState, setViewDetailsState] = useState<TaskDayOccurrencesModalState | null>(
    null,
  )
  const [dataFields, setDataFields] = useState<WebInstrumentsTaskLogDataField[] | null>(null)
  const [logContext, setLogContext] = useState<WebDashboardTaskLogContext | null>(null)
  const [dataFieldsLoading, setDataFieldsLoading] = useState(false)
  const { errors: pageErrors } = usePage<
    SharedProps & { errors?: Record<string, string | string[]> }
  >().props
  const abortRef = useRef<AbortController | null>(null)
  const taskNotes = useTaskNotes(
    record.instrumentTaskId,
    record.hasTaskNotes && (logOpen || viewDetailsState !== null),
  )

  const canPersistLotSelection = useCan(Resources.LOT_MANAGEMENT, Actions.UPDATE)
  const canRetireLot = useCan(Resources.LOT_MANAGEMENT, Actions.UPDATE)

  const fetchLogContext = useCallback(
    (taskId: number, instrumentId: number | null, signal?: AbortSignal) =>
      apiClient
        .get(tasksDashboardLogContextPath(taskId), {
          params: { instrument_id: instrumentId ?? undefined },
          signal,
        })
        .then((response) => {
          const context = response.data as WebDashboardTaskLogContext
          setLogContext(context)
          return context
        }),
    [],
  )

  const fetchLogData = useCallback(
    (taskId: number, instrumentId: number | null) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const cachedFields = dataFieldsCache.get(taskId)
      // Spinner only on first open; reopen / post-mutation refetch keeps the form mounted.
      const isInitialLoad = dataFields === null || logContext === null
      if (isInitialLoad) setDataFieldsLoading(true)

      // Data fields are stable; lot/supply context is not — always refetch so instrument
      // selection and Dashboard modal saves unlock completion without F5 (REBUILD-438).
      const fieldsRequest = cachedFields
        ? Promise.resolve().then(() => {
            setDataFields(cachedFields)
          })
        : apiClient
            .get(tasksDashboardDataFieldsPath(taskId), { signal: controller.signal })
            .then((response) => {
              const fields = response.data as WebInstrumentsTaskLogDataField[]
              dataFieldsCache.set(taskId, fields)
              setDataFields(fields)
            })

      const contextRequest = fetchLogContext(taskId, instrumentId, controller.signal)

      Promise.all([fieldsRequest, contextRequest])
        .catch((error) => {
          if (!controller.signal.aborted) {
            console.error('Failed to load task log context', error)
            setDataFields((previousFields) => previousFields ?? [])
          }
        })
        .finally(() => {
          if (!controller.signal.aborted && isInitialLoad) setDataFieldsLoading(false)
        })
    },
    [fetchLogContext, dataFields, logContext],
  )

  const refreshLogContextAfterLotsMutation = useCallback(() => {
    if (record.instrumentTaskId == null) return
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    void fetchLogContext(record.instrumentTaskId, record.instrumentId, controller.signal).catch(
      (error) => {
        if (!controller.signal.aborted) {
          console.error('Failed to refresh task log context after supply change', error)
        }
      },
    )
  }, [fetchLogContext, record.instrumentTaskId, record.instrumentId])

  useEffect(() => () => abortRef.current?.abort(), [])

  const handleToggleLog = useCallback(() => {
    setLogOpen((previousOpen) => {
      const nextOpen = !previousOpen
      if (nextOpen && record.instrumentTaskId != null) {
        fetchLogData(record.instrumentTaskId, record.instrumentId)
      }
      return nextOpen
    })
  }, [record.instrumentTaskId, record.instrumentId, fetchLogData])

  const statusCode = (record.statusCode ?? 'RDY') as StatusCode
  const config = SECTION_CONFIG[statusCode] ?? SECTION_CONFIG.RDY

  const location = [record.siteName, record.departmentName, record.unitName]
    .filter(Boolean)
    .join(' / ')

  const linkedLotRow: LotRow | null =
    logContext?.linkedLot != null
      ? {
          id: Number(logContext.linkedLot.id),
          name: logContext.linkedLot.name as string | null,
          lotNumber: logContext.linkedLot.lotNumber,
          expirationDate: logContext.linkedLot.expirationDate,
          currentLotDetailId: logContext.currentLotDetailId,
          lifecycleStatus: logContext.linkedLot.lifecycleStatus,
          isLotRetired: logContext.linkedLot.isLotRetired,
          isLotDiscarded: logContext.linkedLot.isLotDiscarded,
        }
      : null

  const usedLotRows: LotRow[] = (logContext?.usedLots ?? []).map((usedLot) => ({
    id: Number(usedLot.id),
    name: usedLot.name as string | null,
    lotNumber: usedLot.lotNumber,
    expirationDate: usedLot.expirationDate,
    currentLotDetailId: usedLot.currentLotDetailId,
    lifecycleStatus: usedLot.lifecycleStatus,
    isLotRetired: usedLot.isLotRetired,
    isLotDiscarded: usedLot.isLotDiscarded,
  }))

  const lotValidityWarnings = buildLotValidityWarnings({
    linkedLot: linkedLotRow,
    usedLots: usedLotRows,
    dataFields: dataFields ?? [],
  })

  const lotsGroupedByLot = logContext?.lots ?? []
  const instrumentId = record.instrumentId
  const instrumentTaskId = record.instrumentTaskId

  const lotModalSlots =
    canPersistLotSelection && instrumentId != null && instrumentTaskId != null
      ? (() => {
          const slots = new Map<number, React.ReactNode>()
          const allLotIds = [
            ...(linkedLotRow && !linkedLotRow.isLotDiscarded ? [linkedLotRow.id] : []),
            ...usedLotRows
              .filter((usedLot) => !usedLot.isLotDiscarded)
              .map((usedLot) => usedLot.id),
          ]
          for (const lotId of allLotIds) {
            const currentDetailId =
              lotId === linkedLotRow?.id
                ? linkedLotRow.currentLotDetailId
                : (usedLotRows.find((usedLot) => usedLot.id === lotId)?.currentLotDetailId ?? null)
            slots.set(
              lotId,
              <ControlsReagentsLotsModal
                key={lotId}
                instrumentNumericId={instrumentId}
                instrumentTaskNumericId={instrumentTaskId}
                instrumentTaskLabel={String(record.taskName ?? '')}
                lotId={lotId}
                lotsGroupedByLot={lotsGroupedByLot}
                defaultSelectedLotDetailId={currentDetailId}
                allowsLotRetirementFromInstrumentPolicy={canRetireLot}
                triggerLabel={t('instruments.show.tasks.selectLotAction')}
                inertiaReloadOnly={DASHBOARD_LOT_MODAL_INERTIA_RELOAD_ONLY}
                onLotsMutation={refreshLogContextAfterLotsMutation}
              />,
            )
          }
          return slots
        })()
      : new Map<number, React.ReactNode>()

  // Secondary escape hatch when the user cannot persist lot selection from the Dashboard.
  const selectSupplyOnInstrumentSlot =
    !canPersistLotSelection && lotValidityWarnings.length > 0 ? (
      <Link
        href={record.viewInstrumentHref}
        className="text-sm font-medium text-primary underline-offset-4 hover:underline"
      >
        {t('taskQueue.card.selectSupply')}
      </Link>
    ) : undefined

  // REBUILD-531: prefer the per-instrument effective code carried on the record (instrument
  // override wins) over the tenant-wide prop; the dashboard lists mixed OL/PR instruments, so a
  // single global code is wrong. Fall back to the prop for safety if the field is absent.
  const effectiveLogAuthenticationCode = record.logAuthenticationCode ?? logAuthenticationCode

  return (
    <Card className={cn('overflow-hidden', config.cardClass)}>
      <CardHeader className="px-4 py-2">
        <div className="flex items-center gap-2">
          {config.icon}
          <span className="text-xs font-medium text-muted-foreground">{config.label()}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 px-4 pb-3 pt-0">
        {/* Instrument info row — hidden when parent InstrumentHeader is rendered */}
        {!hideInstrumentHeader && (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Link
                href={record.viewInstrumentHref}
                className="font-bold text-primary underline-offset-4 hover:underline truncate block"
              >
                {record.instrumentLabel}
              </Link>
              <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
                {record.serialNumber && (
                  <span>
                    {t('taskQueue.card.serialNumber')} {record.serialNumber}
                  </span>
                )}
                {location && <span>{location}</span>}
              </div>
            </div>
            <div className="shrink-0 text-right text-sm">
              <Link
                href={record.viewMaintenanceLogsHref}
                className="text-primary underline-offset-4 hover:underline"
              >
                {t('taskQueue.card.viewLogs')}
              </Link>
            </div>
          </div>
        )}

        {/* Task name */}
        {record.taskName && <span className="text-sm font-medium">{record.taskName}</span>}

        {/* Failed Needs Rerun — same copy as instrument Tasks; NPT gate from E51 helper */}
        {showsFailedNeedsRerunRibbon(record.latestQcPassed, record.latestIsNpt) && (
          <Badge
            variant="destructive"
            className="text-xs w-fit gap-1"
            role="status"
            data-testid="failed-needs-rerun-badge"
          >
            <strong className="font-semibold">
              {t('instruments.show.tasks.failedNeedsRerun.label')}
            </strong>
            <span className="font-normal">{t('instruments.show.tasks.failedNeedsRerun.hint')}</span>
          </Badge>
        )}

        {/* Tags */}
        {record.taskTags && record.taskTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {record.taskTags.map((tag) => (
              <Badge key={tag.id} variant="secondary" className="text-xs">
                {tag.name}
              </Badge>
            ))}
          </div>
        )}

        {/* Interval type — inline-expandable frequency inspect */}
        {(record.intervalTypeDescription || record.frequencyTypeCode) && (
          <FrequencyContextPanel
            frequencyTypeCode={record.frequencyTypeCode}
            intervalTypeDescription={record.intervalTypeDescription}
            frequencyRrule={record.frequencyRrule}
            leadTimeIntervalHours={record.leadTimeIntervalHours}
            leadTimeUnit={record.leadTimeUnit}
            lateTimeIntervalHours={record.lateTimeIntervalHours}
            lateTimeUnit={record.lateTimeUnit}
            fixedScheduleStartTime={record.fixedScheduleStartTime}
            endsAtMidnight={record.endsAtMidnight}
          />
        )}

        {/* Occurrences Dialog — matches calendar day-click behaviour (Rebuild-280).
            The "View Details" trigger lives inside the Log Task expansion (REBUILD-501); the
            dialog itself stays mounted here (controlled by viewDetailsState) regardless of
            collapse state so it can open from the expansion. */}
        {record.instrumentId != null && (
          <TaskDayOccurrencesDialog
            instrumentId={record.instrumentId}
            instrumentLabel={record.instrumentLabel}
            serialNumber={record.serialNumber}
            modalState={viewDetailsState && { ...viewDetailsState, taskNotes }}
            onClose={() => setViewDetailsState(null)}
            // A void inside the dialog fires onReviewSuccess (finishVoid -> handleActionComplete).
            // On the dashboard the void changes the task's status/section, so the reload below
            // remounts this card and tears the dialog down anyway. Close the dialog FIRST so the
            // dialog's own post-void re-fetch cannot briefly repaint the occurrence as an NC row
            // with Resolve/Ignore/Confirm buttons before it disappears (flash on void). Then
            // reload the dashboard props so last_log_id / last_performed / NC count refresh
            // immediately — a voided log must not remain the "last performed" link and a repeat
            // "View Details" must not 404 into "no longer available" (REBUILD-217).
            onReviewSuccess={() => {
              setViewDetailsState(null)
              reloadTasksDashboardProps()
            }}
          />
        )}

        {/* Log Task button */}
        {record.logHref && (
          <div className="border-t pt-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleToggleLog}
              aria-expanded={logOpen}
            >
              <ClipboardList className="h-4 w-4" />
              {t('taskQueue.card.logTask')}
              {logOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {logOpen && (
              <div className="mt-3 rounded-md border bg-background p-3 space-y-3">
                {/* Last Performed + Notes moved off the collapsed ribbon into the Log Task
                    expansion (REBUILD-501). Last Performed always shows (incl. the empty
                    never-performed "—"); Notes render only when present. */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      {t('taskQueue.card.lastPerformed')}{' '}
                      {record.lastPerformedAt
                        ? formatDateTime(record.lastPerformedAt, record.timeZoneName)
                        : '—'}
                    </span>
                    {record.lastLogId && (
                      <button
                        type="button"
                        onClick={() =>
                          setViewDetailsState({
                            instrumentTaskId: record.instrumentTaskId,
                            taskName: record.taskName ?? '',
                            taskNotes: null,
                            date: record.lastPerformedAt?.split('T')[0] ?? '',
                            intervalTypeDescription: record.intervalTypeDescription ?? null,
                            dataFields: [],
                          })
                        }
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {t('taskQueue.card.viewDetails')}
                      </button>
                    )}
                  </div>

                  {/* Task notes — shared rich-text panel (REBUILD-377); hidden when empty */}
                  <TaskNotesPanel notes={taskNotes} className="bg-muted/40" />
                </div>

                {dataFieldsLoading || dataFields === null || logContext === null ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <TaskLogForm
                    mode="new"
                    dataFields={dataFields}
                    loggerOptions={loggerOptions}
                    complianceWindow={{
                      intervalHours: null,
                      lateTimeIntervalHours: null,
                      leadTimeIntervalHours: null,
                      isFixedSchedule: false,
                      lastCompletedAt: record.lastPerformedAt ?? null,
                      taskStatusCode: record.statusCode ?? null,
                      nextOccurrence: null,
                      windowEnd: null,
                      leadDate: null,
                    }}
                    existingTaskLog={null}
                    formAction={record.logHref}
                    formMethod="post"
                    isInline
                    returnTo={window.location.pathname + window.location.search}
                    // Same E45-S3 pattern as Instrument Show: scope the inline submit visit so
                    // presence props refresh in one request (E50-S3 / REBUILD-433).
                    reloadOnly={[...TASKS_DASHBOARD_RELOAD_ONLY]}
                    logAuthenticationCode={effectiveLogAuthenticationCode}
                    siteTimeZone={record.timeZoneName}
                    instrumentId={record.instrumentId}
                    linkedLot={linkedLotRow}
                    usedLots={usedLotRows}
                    lotValidityWarnings={lotValidityWarnings}
                    lotModalSlots={lotModalSlots}
                    changeLotSlot={selectSupplyOnInstrumentSlot}
                    errors={pageErrors}
                    onSuccess={() => setLogOpen(false)}
                    onCancel={() => setLogOpen(false)}
                    dayOff={record.dayOff}
                    scheduleLevel={record.scheduleLevel}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default TaskQueueCard
